import type { FormEventHandler, PropsWithChildren } from 'react';
import React from 'react';
import type { FieldError, Merge } from 'react-hook-form';
import type { NewContact } from '../../assets/icons/new-contact.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Heading } from '../../components/ui/heading.tsx';
import { Label } from '../../components/ui/label.tsx';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import { Dialog } from './dialog.tsx';

type IconType = typeof NewContact;

type HeaderProps = {
    title: string | React.ReactNode | React.ReactNode[];
    subtitle?: string | React.ReactNode | React.ReactNode[];
    icon?: IconType;
};

const Header: React.FC<HeaderProps> = ({ title, subtitle, icon: Icon }) => {
    return (
        <div className="border-b border-neutral-200 px-5 py-4">
            <div className="mx-auto flex w-full max-w-2xl flex-row items-start gap-2 text-neutral-700">
                {Icon && (
                    <div className="mt-0.5 flex h-5 w-5 min-w-5 items-center justify-start">
                        <Icon className="h-5 w-5" />
                    </div>
                )}
                <div className="flex flex-col space-y-0.5">
                    <Heading variant="h1" className="pr-6 text-neutral-900">
                        {title}
                    </Heading>
                    {subtitle && <Dialog.Description>{subtitle}</Dialog.Description>}
                </div>
            </div>
        </div>
    );
};

type ItemListProps = {
    children: React.ReactNode;
    className?: string;
    grid?: boolean;
    subgrid?: boolean;
    col?: boolean;
};

const ItemList: React.FC<ItemListProps> = ({ children, className, grid, subgrid, col }) => {
    return (
        <div
            className={cn(
                'group flex w-full flex-col gap-3',
                grid && 'is-grid grid grid-cols-[auto_1fr]',
                subgrid && 'is-grid grid grid-cols-subgrid col-span-2',
                col && 'is-col',
                className,
            )}
        >
            {children}
        </div>
    );
};

type GroupProps = {
    children: React.ReactNode;
    className?: string;
};

// Wraps multiple subgrid `ItemList`s so their label columns share the same width.
const Group: React.FC<GroupProps> = ({ children, className }) => {
    return <div className={cn('grid w-full grid-cols-[auto_1fr] gap-3', className)}>{children}</div>;
};

type PanelProps = {
    children: React.ReactNode;
    className?: string;
};

// A rounded white panel that passes the parent `Group`'s columns through to its children via subgrid.
const Panel: React.FC<PanelProps> = ({ children, className }) => {
    return (
        <div
            className={cn(
                'col-span-2 grid grid-cols-subgrid rounded-lg bg-white py-2 px-3 shadow-xs',
                className,
            )}
        >
            {children}
        </div>
    );
};

type ItemProps = {
    children: React.ReactNode | React.ReactNode[];
    className?: string;
    col?: boolean;
};

const Item: React.FC<ItemProps> = ({ children, className, col }) => {
    return (
        <div
            className={cn(
                'flex flex-row gap-3 flex-wrap',
                // GRID STYLE
                'group-[.is-grid]:col-span-2 group-[.is-grid]:grid group-[.is-grid]:grid-cols-subgrid',
                'group-[.is-col]:gap-2 group-[.is-col]:flex-col group-[.is-col]:items-stretch',
                col && 'flex-col items-stretch',
                className,
            )}
        >
            {children}
        </div>
    );
};

type BodyProps = {
    children: React.ReactNode | React.ReactNode[];
    className?: string;
};

const Body: React.FC<BodyProps> = ({ children, className = '' }) => {
    return <div className={cn('flex flex-1', className)}>{children}</div>;
};

type ContentProps = {
    children: React.ReactNode;
    className?: string;
};

const Content: React.FC<ContentProps> = ({ children, className }) => {
    return (
        <div
            data-dialog-content
            className={cn('flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3', className)}
        >
            {children}
        </div>
    );
};

type SpacerProps = {
    className?: string;
};

const Spacer: React.FC<SpacerProps> = ({ className = '' }) => {
    return <div className={cn('h-3', className)} />;
};

type ErrorProps = {
    error?: string | FieldError | Merge<FieldError, any>;
};

const Error: React.FC<ErrorProps> = ({ error }) => {
    if (!error) {
        return null;
    }

    return (
        <div className={cn('mt-0.5 flex shrink-0 grow-0 basis-full pl-1 text-sm text-red-600')}>
            {typeof error === 'string' ? error : error.message}
        </div>
    );
};

type SubmitBlockProps = {
    disabled?: boolean;
    isLoading?: boolean;
    onCancel?: () => void;
    onSubmit?: () => void;
    submitText?: string;
    variant?: 'primary' | 'danger';
};

const SubmitBlock: React.FC<SubmitBlockProps> = ({
    onCancel,
    isLoading = false,
    onSubmit,
    submitText,
    disabled,
    variant = 'primary',
}) => {
    const { t } = useLocale();

    return (
        <div className="flex items-center justify-end px-4 py-3 bg-white border-t">
            <div className="flex gap-3">
                {onCancel && (
                    <Button type="button" onClick={onCancel} variant="subtle" className="min-w-24">
                        {t('form__cancel', { ns: 'common' })}
                    </Button>
                )}
                <Button
                    variant={variant}
                    isLoading={isLoading}
                    disabled={disabled}
                    className="min-w-24"
                    type={onSubmit ? 'button' : 'submit'}
                    onClick={onSubmit}
                >
                    {submitText ? submitText : t('form__submit', { ns: 'common' })}
                </Button>
            </div>
        </div>
    );
};

const DialogFormGridItemList = ({ children }: PropsWithChildren) => {
    const gridCols = React.Children.toArray(children).filter((child) => React.isValidElement(child)).length;

    return (
        <div
            className={cn(`grid w-full grid-cols-2 gap-3`, {
                'grid-cols-1': gridCols === 1,
            })}
        >
            {children}
        </div>
    );
};

type DialogFormProps = {
    className?: string;
    onSubmit?: FormEventHandler<HTMLFormElement>;
} & React.PropsWithChildren;

export const DialogForm = ({ children, className, onSubmit }: DialogFormProps) => {
    return (
        <form className={cn('flex min-h-0 w-screen flex-col sm:max-w-2xl', className)} onSubmit={onSubmit}>
            {children}
        </form>
    );
};

DialogForm.Header = Header;
DialogForm.ItemList = ItemList;
DialogForm.Item = Item;
DialogForm.Group = Group;
DialogForm.Panel = Panel;
DialogForm.Grid = DialogFormGridItemList;
DialogForm.Label = Label;
DialogForm.Body = Body;
DialogForm.Content = Content;
DialogForm.Spacer = Spacer;
DialogForm.Error = Error;
DialogForm.SubmitBlock = SubmitBlock;
